import { DateTime } from 'luxon'

import { MONTH_DAY_AND_YEAR_FORMAT, ONLY_YEAR_FORMAT, SHORT_MONTH_AND_YEAR_FORMAT } from '@/constants/dateTimeFormats'
import type { Activation } from '@/features/activation/types/activation'
import type { ChartData } from '@/features/activation/types/activationsChartData'
import { ChartTimeUnit } from '@/features/activation/types/chartTimeUnit'
import type { MarketProgramType } from '@/types/marketProgramType'

export function getActivationsInTimeRange(activations: Activation[], toDate: DateTime): Activation[] {
  return activations.filter(
    (activation) => activation.startedAt && activation.endedAt && DateTime.fromISO(activation.endedAt) <= toDate,
  )
}

export function getDistinctMarketPrograms(activations: Activation[]): Set<MarketProgramType> {
  const nonDistinctMarketPrograms = activations
    .map((activation) => activation.marketProgram)
    .filter(Boolean) as MarketProgramType[]
  return new Set<MarketProgramType>(nonDistinctMarketPrograms)
}

export function countActivationsPerTimeLabelAndMarketProgram(
  timeUnit: ChartTimeUnit,
  activations: Activation[],
  marketPrograms: MarketProgramType[],
  fromDate: DateTime,
  toDate: DateTime,
): ChartData[] {
  const dataByTimeUnit = initializeMapByTimeUnit(timeUnit, marketPrograms, fromDate, toDate)

  for (const activation of activations) {
    const activationTimeLabel = getTimeLabel(timeUnit, DateTime.fromISO(activation.endedAt as string))

    const dataForTimeLabel = dataByTimeUnit.get(activationTimeLabel)
    if (dataForTimeLabel !== undefined && activation.marketProgram !== undefined) {
      dataForTimeLabel[activation.marketProgram] += 1
    }
  }
  return Array.from(dataByTimeUnit.values())
}

function initializeMapByTimeUnit(
  timeUnit: ChartTimeUnit,
  marketPrograms: MarketProgramType[],
  fromDate: DateTime,
  toDate: DateTime,
): Map<string, ChartData> {
  const dataByTimeLabel = new Map<string, ChartData>()

  const timeLabels = getTimeLabelsInTimeRange(timeUnit, fromDate.toJSDate(), toDate.toJSDate())

  for (const timeLabel of timeLabels) {
    const emptyChartData: ChartData = { timeLabel }
    for (const program of marketPrograms) {
      emptyChartData[program] = 0
    }
    dataByTimeLabel.set(timeLabel, emptyChartData)
  }

  return dataByTimeLabel
}

function getTimeLabel(timeUnit: ChartTimeUnit, date: DateTime) {
  switch (timeUnit) {
    case ChartTimeUnit.DAY:
      return date.toFormat(MONTH_DAY_AND_YEAR_FORMAT)
    case ChartTimeUnit.YEAR:
      return date.toFormat(ONLY_YEAR_FORMAT)
    default:
      return date.toFormat(SHORT_MONTH_AND_YEAR_FORMAT)
  }
}

function getTimeLabelsInTimeRange(timeUnit: ChartTimeUnit, fromDate: Date, toDate: Date): string[] {
  const timeLabelsInSelectedRange: string[] = []
  const current = new Date(fromDate) // clone

  while (current <= toDate) {
    const dt = DateTime.fromJSDate(current)
    const timeLabel = getTimeLabel(timeUnit, dt)
    timeLabelsInSelectedRange.push(timeLabel)

    current.setDate(current.getDate() + 1)
  }

  return timeLabelsInSelectedRange
}
